import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiError } from "@/lib/network";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const META = { olt: ["OLT", "olt"], odc: ["ODC", "odc"], odp: ["ODP", "odp"], cable: ["Jalur Kabel", "cables"], splitter: ["Splitter", "splitters"], otb: ["OTB", "otb"] };

// Tombol hapus permanen khusus Admin; backend tetap memvalidasi role & dependensi.
export function DeleteInfraButton({ kind, item, me, onDeleted, iconOnly = false, tid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chk, setChk] = useState(null);
  if (!me?.is_admin || !item) return null;
  const [label, path] = META[kind];
  const checkable = kind === "odc" || kind === "odp";
  const openDlg = () => {
    setChk(null); setOpen(true);
    if (checkable) api.get(`/network/${path}/${item.id}/delete-check`).then((r) => setChk(r.data)).catch(() => setChk({ error: true }));
  };
  const blocked = checkable && (!chk || chk.error || chk.active.length > 0);
  const run = async () => {
    setBusy(true);
    try {
      await api.delete(`/network/${path}/${item.id}`);
      toast.success(`${label} ${item.kode} dihapus`);
      setOpen(false);
      onDeleted?.(item);
    } catch (err) {
      toast.error(apiError(err), { duration: 8000 });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {iconOnly
        ? <Button size="icon" variant="ghost" onClick={openDlg} title={`Hapus ${label}`} data-testid={tid || `${kind}-delete-${item.kode}`}>
            <Trash2 className="h-4 w-4 text-[#DC2626]" /></Button>
        : <Button size="sm" variant="outline" className="border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2]" onClick={openDlg} data-testid={`${kind}-delete-btn`}>
            <Trash2 className="mr-1 h-3.5 w-3.5" />Hapus</Button>}
      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent data-testid={`${kind}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {label} {item.kode}?</AlertDialogTitle>
            <AlertDialogDescription>
              {label} <b>{item.kode}</b>{item.nama ? ` — ${item.nama}` : ""} akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.{" "}
              {checkable ? "Penghapusan ditolak bila masih ada relasi aktif, ODP anak, atau jalur kabel." : "Penghapusan ditolak bila masih ada data yang terhubung atau riwayat."}
            </AlertDialogDescription>
            {checkable && <DeleteCheck chk={chk} />}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`${kind}-delete-cancel`}>Batal</AlertDialogCancel>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" disabled={busy || blocked} onClick={run} data-testid={`${kind}-delete-confirm`}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Hapus</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeleteCheck({ chk }) {
  if (!chk) return <p className="text-xs text-[#6B7280]" data-testid="delete-check-loading">Memeriksa ketergantungan…</p>;
  if (chk.error) return <p className="text-xs text-[#B91C1C]" data-testid="delete-check-error">Gagal memeriksa ketergantungan. Coba lagi.</p>;
  if (chk.active.length) return (
    <div className="rounded-md border border-[#FCA5A5] bg-[#FEF2F2] p-2 text-xs text-[#B91C1C]" role="alert" data-testid="delete-check-active">
      <b>Tidak dapat dihapus — masih ada ketergantungan aktif:</b> {chk.active.join("; ")}. Lepaskan terlebih dahulu.</div>);
  if (chk.history.length) return (
    <div className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] p-2 text-xs text-[#92400E]" data-testid="delete-check-history">
      <b>Perangkat ini memiliki riwayat:</b> {chk.history.join("; ")}. Riwayat tetap tersimpan, tetapi kode/nama perangkat tidak lagi tampil di riwayat setelah dihapus.
      Disarankan gunakan <b>Arsipkan</b> agar riwayat tetap lengkap.</div>);
  return <p className="rounded-md bg-[#F0FDF4] p-2 text-xs text-[#166534]" data-testid="delete-check-clean">Tidak ada relasi maupun riwayat. Aman dihapus permanen.</p>;
}
